import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { Button, Input, Select, useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useLocation } from 'react-router-dom';

import AddIcon from '@/assets/AddIcon';
import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from '@/common/constants';
import ModalDelete from '@/components/Modals/ModalDelete';
import TableShipOwner from '@/components/Tables/TableShipOwner';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { setDataTotalNewShipOperator } from '@/redux/slices/notificationSlice';
import { deleteShipOwner, getListShipOwner } from '@/services/shipOwnerService';
import { downloadFile } from '@/services/downloadService';
import { getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';

import '../styles/ListPage.css';

const ShipOwnerList = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);
    const toast = useToast();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const { currentPageNumberList, currentPageSizeList } = location.state || {}

    // Read initial values from URL params
    const urlSearchParams = new URLSearchParams(window.location.search);
    const initialSearch = urlSearchParams.get('search') || '';
    const initialStatus = urlSearchParams.get('status') || '';
    const initialPageNumber = Number(urlSearchParams.get('pageNumber')) || currentPageNumberList || DEFAULT_PAGE_NUMBER;
    const initialPageSize = Number(urlSearchParams.get('pageSize')) || currentPageSizeList || DEFAULT_PAGE_SIZE;

    const [searchValue, setSearchValue] = useState(initialSearch);
    const [statusValue, setStatusValue] = useState(initialStatus);
    const [currentPageNumber, setCurrentPageNumber] = useState(initialPageNumber);
    const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [dataList, setDataList] = useState([]) as any;
    const [id, setId] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const showToast = (title: string, description: string, status: 'success' | 'error') => {
        toast({
            title,
            description,
            status,
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleDelete = (id: string) => {
        setId(id);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        const obj = {
            shipOwnerId: id,
            modifiedBy: name || 'System',
        };

        try {
            const response = await deleteShipOwner(obj) as any;
            if (response.status === HttpStatusCode.Ok) {
                getData();
                showToast('Success', "We've deleted the ship operator.", 'success');
            } else {
                showToast('Error', 'Failed to delete ship operator.', 'error');
            }
        } catch {
            showToast('Error', 'Failed to delete the ship operator.', 'error');
        } finally {
            onClose();
        }
    };

    const handleDownload = async (id: string) => {
        try {
					await downloadFile(id);
        } catch {
            showToast('Error', 'Failed to download file.', 'error');
        }
    };

    const setLatestCounter = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewShipOperator(responseCounter?.data?.shipOperator || 0));
        }
    }

    const getData = async () => {
        setIsLoading(true);

        // Use URL params for search and status
        const urlParams = new URLSearchParams();
        if (searchValue) urlParams.append('search', searchValue);
        if (statusValue) urlParams.append('status', statusValue);

        try {
            const response = await getListShipOwner({
                search: searchValue,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize,
                status: statusValue,
            }) as any;
            if (response.status === HttpStatusCode.Ok) {
                setDataList(response.data);
                setTotalPageNumber(response.totalPage);
                setCurrentPageNumber(response.pageNumber);
                setHasNextPage(response.hasNextPage);
                setLatestCounter();
            } else {
                showToast('Error', response.error.message, response.error.status || 'error');
            }
        } catch {
            showToast('Error', 'Failed to fetch ship operators.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

       // Helper to update URL params
    const updateUrlParams = (params: Record<string, string | number | undefined>) => {
        const url = new URL(window.location.href);
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
                url.searchParams.set(key, String(value));
            } else {
                url.searchParams.delete(key);
            }
        });
        navigate(`${url.pathname}${url.search}`, { replace: true });
    };

    // On mount, set form state from URL params and fetch data
    useEffect(() => {
        setSearchValue(initialSearch);
        setStatusValue(initialStatus);
        setCurrentPageNumber(initialPageNumber);
        setCurrentPageSize(initialPageSize);
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch data when page size or number changes
    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="table-filter-input filter-input-width-7">
                            <Input
                                placeholder="Search"
                                value={searchValue}
                                onChange={(e) => {
                                    setSearchValue(e.target.value);
                                    updateUrlParams({
                                        search: e.target.value,
                                        status: statusValue,
                                        pageNumber: currentPageNumber,
                                        pageSize: currentPageSize
                                    });
                                }}
                            />
                        </div>
                        <div className="table-filter-input filter-input-width-4">
                            <Select
                                placeholder="All Status"
                                value={statusValue}
                                onChange={(e) => {
                                    setStatusValue(e.target.value);
                                    updateUrlParams({
                                        search: searchValue,
                                        status: e.target.value,
                                        pageNumber: currentPageNumber,
                                        pageSize: currentPageSize
                                    });
                                }}
                            >
                                <option value="1">New</option>
                                <option value="2">In Review</option>
                                <option value="3">Active</option>
                                <option value="4">Rejected</option>
                                <option value="5">Inactive</option>
                            </Select>
                        </div>
                        <div>
                            <Button
                                className='table-search-button'
                                bgColor={COLORS.BUTTON.BACKGROUND}
                                color={COLORS.BUTTON.FOREGROUND}
                                borderRadius={10}
                                onClick={() => getData()}
                            >
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button
                            leftIcon={<AddIcon />}
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            borderRadius={10}
                            className='table-add-button'
                            onClick={() => navigate('/manage-ship-operator/add')}
                        >
                            Add Ship Operator
                        </Button> 
                        <Button
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            borderRadius={10}
                            onClick={() => navigate('/manage-ship-operator/grouped-list')}
                        >
                            Grouped List
                        </Button> 
                    </div>
                </div>
                <TableShipOwner
                    dataList={dataList}
                    isLoading={isLoading}
                    handleEdit={(id: string) => navigate('/manage-ship-operator/edit', { state: { id } })}
                    handleDelete={handleDelete}
                    currentPageSize={currentPageSize}
                    handleChangePageSize={setCurrentPageSize}
                    handleChangePageNumber={setCurrentPageNumber}
                    currentPageNumber={currentPageNumber}
                    totalPageNumber={totalPageNumber}
                    hasNextPage={hasNextPage}
                    handleDownload={handleDownload}
                    handleReview={(id: string) => navigate('/manage-ship-operator/review', { state: { id } })}
                />
            </div>
        </>
    );
};

export default ShipOwnerList;
